import React, { useState } from "react";
import {
  FiSearch,
  FiTrash2,
  FiRefreshCw,
  FiChevronLeft,
  FiChevronRight,
  FiAlertTriangle,
  FiMessageSquare,
  FiUsers,
  FiCheckCircle,
  FiXCircle,
} from "react-icons/fi";
import {
  useGetAdminCommunitiesQuery,
  useDeleteAdminCommunityMutation,
  useSyncCommunityMembersMutation,
} from "../../redux/slices/api/adminApiSlice";
import { Bars } from "react-loader-spinner";
import { toast } from "react-toastify";
import WalletDisplay from "../../components/common/WalletDisplay";

const AdminCommunities = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [selectedCommunity, setSelectedCommunity] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  const { data, isLoading, error, refetch } = useGetAdminCommunitiesQuery({
    page,
    limit: 20,
    search,
  });

  const [deleteCommunity, { isLoading: isDeleting }] =
    useDeleteAdminCommunityMutation();

  const [syncCommunityMembers, { isLoading: isSyncing }] =
    useSyncCommunityMembersMutation();

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  const handleDeleteClick = (community) => {
    setSelectedCommunity(community);
    setShowDeleteModal(true);
  };

  const handleSyncClick = (community) => {
    setSelectedCommunity(community);
    setSyncResult(null);
    setShowSyncModal(true);
  };

  const handleConfirmSync = async () => {
    if (!selectedCommunity) return;

    try {
      const result = await syncCommunityMembers(selectedCommunity._id).unwrap();
      setSyncResult(result);
      toast.success(`Sync completed: ${result.added} added, ${result.updated} updated`);
      refetch();
    } catch (err) {
      setSyncResult({ 
        synced: false, 
        error: err?.data?.message || "Failed to sync members from blockchain" 
      });
      toast.error(err?.data?.message || "Failed to sync members");
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedCommunity) return;

    try {
      await deleteCommunity(selectedCommunity._id).unwrap();
      toast.success("Community deleted successfully");
      setShowDeleteModal(false);
      setSelectedCommunity(null);
    } catch (err) {
      toast.error(err?.data?.message || "Failed to delete community");
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getCreatorName = (community) => {
    const creator = community.creator;
    if (!creator) return "N/A";
    if (creator.fundername) return creator.fundername;
    return `${creator.firstname || ""} ${creator.lastname || ""}`.trim() || "N/A";
  };

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <p className="text-red-600">
          Failed to load communities. Please try again.
        </p>
        <button
          onClick={refetch}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-secondary">Communities</h1>
          <p className="text-gray-500">
            Manage all communities ({data?.pagination?.total || 0} total)
          </p>
        </div>
        <button
          onClick={refetch}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-light-primary transition-colors"
        >
          <FiRefreshCw size={16} />
          Refresh
        </button>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <form onSubmit={handleSearch}>
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by name or description..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
            />
          </div>
        </form>
      </div>

      {/* Communities Grid */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Bars height="40" width="40" color="#8538FF" ariaLabel="loading" />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-gray-700">
                      Community
                    </th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-gray-700">
                      Creator
                    </th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-gray-700">
                      Members
                    </th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-gray-700">
                      Created
                    </th>
                    <th className="text-right px-6 py-4 text-sm font-semibold text-gray-700">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {data?.communities?.map((community) => (
                    <tr key={community._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-pink-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                            {community.image ? (
                              <img
                                src={community.image}
                                alt={community.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <FiMessageSquare className="text-pink-500" size={18} />
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-secondary truncate max-w-[250px]">
                              {community.name}
                            </p>
                            <p className="text-sm text-gray-500 truncate max-w-[250px]">
                              {community.description || "No description"}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden flex-shrink-0">
                            {community.creator?.images?.profile ? (
                              <img
                                src={community.creator.images.profile}
                                alt=""
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <span className="text-primary text-xs font-semibold">
                                {getCreatorName(community)[0]?.toUpperCase() || "?"}
                              </span>
                            )}
                          </div>
                          <span className="text-gray-600 truncate max-w-[120px]">
                            {getCreatorName(community)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-gray-600">
                          {community.members?.length || 0}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-gray-600 text-sm">
                          {formatDate(community.createdAt)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleSyncClick(community)}
                            className="p-2 text-blue-500 hover:bg-blue-100 rounded-lg transition-colors"
                            title="Sync Members from Blockchain"
                          >
                            <FiUsers size={16} />
                          </button>
                          <button
                            onClick={() => handleDeleteClick(community)}
                            className="p-2 text-red-500 hover:bg-red-100 rounded-lg transition-colors"
                            title="Delete Community"
                          >
                            <FiTrash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Empty State */}
            {data?.communities?.length === 0 && (
              <div className="text-center py-12">
                <FiMessageSquare className="mx-auto text-gray-300 mb-4" size={48} />
                <p className="text-gray-500">No communities found</p>
              </div>
            )}

            {/* Pagination */}
            {data?.pagination && data.pagination.pages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                <p className="text-sm text-gray-500">
                  Showing{" "}
                  {(data.pagination.page - 1) * data.pagination.limit + 1} to{" "}
                  {Math.min(
                    data.pagination.page * data.pagination.limit,
                    data.pagination.total
                  )}{" "}
                  of {data.pagination.total} communities
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FiChevronLeft size={16} />
                  </button>
                  <span className="px-3 py-1 text-sm">
                    Page {page} of {data.pagination.pages}
                  </span>
                  <button
                    onClick={() =>
                      setPage((p) => Math.min(data.pagination.pages, p + 1))
                    }
                    disabled={page === data.pagination.pages}
                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FiChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <FiAlertTriangle className="text-red-600" size={24} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-secondary">
                  Delete Community?
                </h3>
                <p className="text-sm text-gray-500 truncate max-w-[250px]">
                  {selectedCommunity?.name}
                </p>
              </div>
            </div>

            <p className="text-gray-600 mb-6">
              This action cannot be undone. The community will be permanently
              deleted.
            </p>

            <div className="flex items-center gap-3 justify-end">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedCommunity(null);
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={isDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {isDeleting && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                )}
                Delete Community
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sync Members Modal */}
      {showSyncModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                <FiUsers className="text-blue-600" size={24} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-secondary">
                  Sync Members from Blockchain
                </h3>
                <p className="text-sm text-gray-500 truncate max-w-[300px]">
                  {selectedCommunity?.name}
                </p>
              </div>
            </div>

            {!syncResult ? (
              <>
                <p className="text-gray-600 mb-4">
                  This will query the blockchain contract to find all NFT owners and sync them to the database. 
                  Use this to recover members who purchased NFTs but aren't showing in the member list.
                </p>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <p className="text-sm text-blue-800">
                    <strong>Contract:</strong>{" "}
                    <span className="font-mono text-xs break-all">
                      {selectedCommunity?.nftDetails?.contractAddress || "Not deployed"}
                    </span>
                  </p>
                  <p className="text-sm text-blue-800 mt-2">
                    <strong>Network:</strong> {selectedCommunity?.blockchain || "Unknown"}
                  </p>
                  <p className="text-sm text-blue-800 mt-2">
                    <strong>Current Members:</strong> {selectedCommunity?.members?.length || 0}
                  </p>
                </div>

                <div className="flex items-center gap-3 justify-end">
                  <button
                    onClick={() => {
                      setShowSyncModal(false);
                      setSelectedCommunity(null);
                      setSyncResult(null);
                    }}
                    className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmSync}
                    disabled={isSyncing || !selectedCommunity?.nftDetails?.contractAddress}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                  >
                    {isSyncing && (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    )}
                    {isSyncing ? "Syncing..." : "Sync Now"}
                  </button>
                </div>
              </>
            ) : (
              <>
                {syncResult.synced ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-green-600 mb-4">
                      <FiCheckCircle size={20} />
                      <span className="font-medium">Sync Completed Successfully</span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="bg-green-50 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-green-600">{syncResult.added}</p>
                        <p className="text-sm text-green-700">Members Added</p>
                      </div>
                      <div className="bg-blue-50 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-blue-600">{syncResult.updated}</p>
                        <p className="text-sm text-blue-700">Members Updated</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-gray-600">{syncResult.skipped}</p>
                        <p className="text-sm text-gray-700">Unchanged</p>
                      </div>
                      <div className="bg-purple-50 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-purple-600">{syncResult.totalOnChain}</p>
                        <p className="text-sm text-purple-700">Total On-Chain</p>
                      </div>
                    </div>

                    {syncResult.syncedMembers?.length > 0 && (
                      <div className="border rounded-lg overflow-hidden">
                        <div className="bg-gray-50 px-4 py-2 border-b">
                          <p className="font-medium text-gray-700 text-sm">Synced Members</p>
                        </div>
                        <div className="max-h-48 overflow-y-auto">
                          {syncResult.syncedMembers.map((member, idx) => (
                            <div key={idx} className="px-4 py-2 border-b last:border-b-0 flex items-center justify-between">
                              <div className="min-w-0 flex-1">
                                <p className="font-medium text-sm truncate">
                                  {member.userName || "Anonymous Wallet"}
                                </p>
                                <p className="text-xs text-gray-500 font-mono truncate">
                                  <WalletDisplay address={member.walletAddress} />
                                </p>
                              </div>
                              <div className="flex items-center gap-2 ml-2">
                                <span className="text-xs text-gray-500">Token #{member.tokenId}</span>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                  member.action === "added" 
                                    ? "bg-green-100 text-green-700" 
                                    : "bg-blue-100 text-blue-700"
                                }`}>
                                  {member.action}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-red-600 mb-4">
                      <FiXCircle size={20} />
                      <span className="font-medium">Sync Failed</span>
                    </div>
                    <p className="text-gray-600">{syncResult.error || syncResult.message}</p>
                  </div>
                )}

                <div className="flex items-center gap-3 justify-end mt-6">
                  <button
                    onClick={() => {
                      setShowSyncModal(false);
                      setSelectedCommunity(null);
                      setSyncResult(null);
                    }}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCommunities;
